import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  FaPlay, FaHeart, FaComment, FaEye, FaLock, 
  FaUpload, FaTimes, FaSpinner, FaLockOpen, 
  FaTrash, FaSyncAlt, FaFilter
} from 'react-icons/fa';
import { Plyr } from 'plyr-react';
import 'plyr-react/plyr.css';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import uploadService from '../../services/upload';
import videoService from '../../services/video';
import toast from 'react-hot-toast';
import Loader from '../common/Loader';
import Modal from '../common/Modal';
import './media.css';

const Videos = () => {
  // States principaux
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [showVideoModal, setShowVideoModal] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [videoToDelete, setVideoToDelete] = useState(null);
  
  // Pagination
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalVideos, setTotalVideos] = useState(0);
  
  // Filtres
  const [filter, setFilter] = useState('all');
  const [showFilters, setShowFilters] = useState(false);
  
  // Upload data
  const [uploadData, setUploadData] = useState({
    titre: '',
    description: '',
    file: null,
    isPrivate: false
  });

  // Refs
  const observerRef = useRef();
  const fileInputRef = useRef();
  const plyrRefs = useRef({});

  const navigate = useNavigate();
  const { user } = useAuth();
  const UPLOADS_URL = process.env.REACT_APP_UPLOADS_URL || 'https://murima.julesferry-ga.com/uploads';

  // Getters d'URL
  const getVideoUrl = (video) => {
    if (!video?.url) return '';
    if (video.url.startsWith('http')) return video.url;
    return `${UPLOADS_URL}/${video.url}`;
  };

  const getAvatarUrl = (avatar) => {
    if (!avatar) return 'https://images.unsplash.com/photo-1529626455594-4ff0802cfb7e';
    if (avatar.startsWith('http')) return avatar;
    return `${UPLOADS_URL}/${avatar}`;
  };

  // Charger les vidéos
  const loadVideos = useCallback(async (reset = false) => {
    if (!user?.id) return;
    
    if (reset) {
      setPage(1);
      setVideos([]);
      setHasMore(true);
    }

    const loadingState = reset ? setLoading : setLoadingMore;
    loadingState(true);

    try {
      const response = await api.get('/get-videos.php', {
        params: { 
          user_id: user.id,
          page: reset ? 1 : page,
          limit: 12,
          filter: filter,
          _t: Date.now()
        }
      });
      
      if (response.data.success) {
        const newVideos = response.data.videos || [];
        const formattedVideos = newVideos.map(video => ({
          id: video.id,
          user_id: video.user_id,
          pseudo: video.pseudo,
          avatar: video.avatar,
          titre: video.titre,
          description: video.description || '',
          url: video.url,
          thumbnail: video.thumbnail || null,
          duree: parseInt(video.duree) || 0,
          vues: parseInt(video.vues) || 0,
          likes: parseInt(video.likes) || 0,
          est_privee: Boolean(video.est_privee),
          date_upload: video.date_upload,
          user_liked: Boolean(video.user_liked),
          can_delete: video.user_id === user.id
        }));

        setVideos(prev => reset ? formattedVideos : [...prev, ...formattedVideos]);
        setHasMore(response.data.hasMore || false);
        setTotalVideos(response.data.total || 0);
        
        if (!reset) {
          setPage(prev => prev + 1);
        }
      }
    } catch (error) {
      console.error('❌ Error loading videos:', error);
      toast.error('Erreur lors du chargement des vidéos');
    } finally {
      loadingState(false);
    }
  }, [user?.id, page, filter]);

  // Confirmation suppression
  const confirmDeleteVideo = (videoId, e) => {
    e.stopPropagation();
    setVideoToDelete(videoId);
    setShowDeleteConfirm(true);
  };

  // Suppression vidéo
  const handleDeleteVideo = async () => {
    if (!videoToDelete) return;

    try {
      const response = await api.post('/delete-video.php', {
        video_id: videoToDelete,
        user_id: user.id
      });

      if (response.data.success) {
        setVideos(prev => prev.filter(v => v.id !== videoToDelete));
        toast.success('Vidéo supprimée');
        
        if (selectedVideo?.id === videoToDelete) {
          setShowVideoModal(false);
          setSelectedVideo(null);
        }
      } else {
        toast.error(response.data.error || 'Erreur lors de la suppression');
      }
    } catch (error) {
      console.error('❌ Delete error:', error);
      toast.error('Erreur réseau');
    } finally {
      setShowDeleteConfirm(false);
      setVideoToDelete(null);
    }
  };

  // Rafraîchir les vidéos
  const refreshVideos = () => {
    loadVideos(true);
    toast.success('Vidéos actualisées');
  };

  // Initial load
  useEffect(() => {
    if (user?.id) {
      loadVideos(true);
    }
  }, [user?.id, filter]);

  // Intersection Observer pour l'infinite scroll
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '20px',
      threshold: 0.1
    };

    observerRef.current = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first.isIntersecting && hasMore && !loadingMore && !loading) {
        loadVideos();
      }
    }, options);

    const sentinel = document.getElementById('sentinel');
    if (sentinel) {
      observerRef.current.observe(sentinel);
    }

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, [hasMore, loadingMore, loading, loadVideos]);

  // Gestionnaire d'upload
  const handleUploadChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    
    if (type === 'file') {
      const file = files[0];
      if (file) {
        const validTypes = ['video/mp4', 'video/quicktime', 'video/x-msvideo'];
        if (!validTypes.includes(file.type)) {
          toast.error('Format non supporté. Utilisez MP4, MOV ou AVI');
          e.target.value = '';
          return;
        }

        if (file.size > 100 * 1024 * 1024) {
          toast.error('La vidéo ne doit pas dépasser 100MB');
          e.target.value = '';
          return;
        }

        setUploadData(prev => ({ ...prev, file }));
      }
    } else if (type === 'checkbox') {
      setUploadData(prev => ({ ...prev, [name]: checked }));
    } else {
      setUploadData(prev => ({ ...prev, [name]: value }));
    }
  };

  // Upload vidéo
  const handleUpload = async (e) => {
    e.preventDefault();
    
    if (!uploadData.file || !uploadData.titre.trim()) {
      toast.error('Veuillez remplir tous les champs');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      const result = await uploadService.uploadVideo(
        uploadData.file,
        uploadData.titre,
        uploadData.isPrivate,
        (progress) => setUploadProgress(progress)
      );

      if (result.success) {
        toast.success(result.message || 'Vidéo publiée avec succès !');
        setShowUploadModal(false);
        resetUploadForm();
        refreshVideos();
      } else {
        toast.error(result.error || 'Erreur lors de l\'upload');
      }
    } catch (error) {
      console.error('❌ Upload error:', error);
      toast.error('Erreur lors de l\'upload');
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  // Like/Unlike
  const handleLike = async (videoId, e) => {
    e?.stopPropagation();
    
    if (!user?.id) {
      toast.error('Connectez-vous pour liker');
      return;
    }

    const video = videos.find(v => v.id === videoId);
    if (!video) return;

    const wasLiked = video.user_liked;
    
    setVideos(prev =>
      prev.map(v =>
        v.id === videoId
          ? {
              ...v,
              likes: wasLiked ? v.likes - 1 : v.likes + 1,
              user_liked: !wasLiked
            }
          : v
      )
    );

    try {
      const result = await videoService.likeVideo(videoId, user.id);

      if (!result.success) {
        setVideos(prev =>
          prev.map(v =>
            v.id === videoId
              ? {
                  ...v,
                  likes: wasLiked ? v.likes + 1 : v.likes - 1,
                  user_liked: wasLiked
                }
              : v
          )
        );
        toast.error(result.error || 'Erreur lors du like');
      }
    } catch (error) {
      setVideos(prev =>
        prev.map(v =>
          v.id === videoId
            ? {
                ...v,
                likes: wasLiked ? v.likes + 1 : v.likes - 1,
                user_liked: wasLiked
              }
            : v
        )
      );
      toast.error('Erreur réseau');
    }
  };

  // Lecture vidéo
  const playVideo = (video) => {
    if (video.est_privee && video.user_id !== user?.id) {
      api.get('/check-access.php', {
        params: {
          video_id: video.id,
          user_id: user?.id
        }
      }).then(response => {
        if (!response.data.hasAccess) {
          toast.error('Cette vidéo est privée et réservée aux matches');
          return;
        }
        setSelectedVideo(video);
        setShowVideoModal(true);
        
        setVideos(prev =>
          prev.map(v =>
            v.id === video.id ? { ...v, vues: v.vues + 1 } : v
          )
        );
        videoService.incrementViews(video.id, user?.id).catch(console.error);
      }).catch(() => toast.error('Impossible de vérifier l\'accès'));
      return;
    }

    setSelectedVideo(video);
    setShowVideoModal(true);
    
    setVideos(prev =>
      prev.map(v =>
        v.id === video.id ? { ...v, vues: v.vues + 1 } : v
      )
    );

    videoService.incrementViews(video.id, user?.id).catch(console.error);
  };

  // Formateurs
  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatNumber = (num) => {
    if (!num) return '0';
    if (num >= 1000000) return (num / 1000000).toFixed(1) + 'M';
    if (num >= 1000) return (num / 1000).toFixed(1) + 'k';
    return num.toString();
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = Math.floor((now - date) / (1000 * 60 * 60 * 24));
    
    if (diff === 0) return "Aujourd'hui";
    if (diff === 1) return 'Hier';
    if (diff < 7) return `Il y a ${diff} jours`;
    if (diff < 30) return `Il y a ${Math.floor(diff / 7)} semaines`;
    return date.toLocaleDateString('fr-FR');
  };

  // Réinitialiser le formulaire d'upload
  const resetUploadForm = () => {
    setUploadData({
      titre: '',
      description: '',
      file: null,
      isPrivate: false
    });
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  if (loading && videos.length === 0) {
    return <Loader />;
  }

  return (
    <div className="media-page">
      {/* Header avec filtres */}
      <div className="media-header">
        <div className="header-left">
          <h1>Vidéos</h1>
          <button 
            className="btn-filter-toggle"
            onClick={() => setShowFilters(!showFilters)}
          >
            <FaFilter />
          </button>
        </div>
        
        <div className="header-actions">
          <button 
            className="btn-refresh" 
            onClick={refreshVideos}
            disabled={loading}
          >
            <FaSyncAlt className={loading ? 'spin' : ''} />
          </button>
          <button 
            className="btn-upload" 
            onClick={() => setShowUploadModal(true)}
          >
            <FaUpload /> Publier
          </button>
        </div>
      </div>

      {/* Filtres */}
      {showFilters && (
        <div className="filters-bar">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Toutes ({totalVideos})
          </button>
          <button 
            className={`filter-btn ${filter === 'mine' ? 'active' : ''}`}
            onClick={() => setFilter('mine')}
          >
            Mes vidéos
          </button>
          <button 
            className={`filter-btn ${filter === 'matches' ? 'active' : ''}`}
            onClick={() => setFilter('matches')}
          >
            Matches
          </button>
        </div>
      )}

      {/* Grille de vidéos */}
      {videos.length === 0 ? (
        <div className="no-media">
          <FaPlay className="no-media-icon" />
          <h3>Aucune vidéo pour le moment</h3>
          <p>Soyez le premier à partager une vidéo !</p>
          <button 
            className="btn-upload-large" 
            onClick={() => setShowUploadModal(true)}
          >
            <FaUpload /> Publier une vidéo
          </button>
        </div>
      ) : (
        <>
          <div className="videos-grid">
            {videos.map(video => (
              <div key={video.id} className="video-card">
                <div className="video-thumbnail">
                  {/* Plyr dans la thumbnail avec tous les contrôles */}
                  <Plyr
                    ref={el => {
                      if (el) plyrRefs.current[video.id] = el;
                    }}
                    source={{
                      type: 'video',
                      sources: [
                        {
                           src: getVideoUrl(video),
                          type: 'video/mp4',
                        }
                      ]
                    }}
                    options={{
                      controls: [
                         'play',           // Lecture/Pause
                        'progress',       // Barre de progression
                        'current-time',   // Temps actuel
                        // 'duration',       // Durée totale
                        'mute',           // Muet
                        'volume',         // Volume
                        'captions',       // Sous-titres
                          // Paramètres
                        // 'pip',            // Picture-in-Picture
                        'airplay',        // AirPlay
                        'fullscreen'      // Plein écran ⭐
                      ],
                      autoplay: false,
                      muted: false,
                      volume: 1,
                      ratio: '4:3',
                      hideControls: false,
                      invertTime: false,
                      toggleInvert: false,
                      debug: false,
                      // Options supplémentaires pour le plein écran
                      fullscreen: {
                        enabled: true,
                        fallback: true,
                        iosNative: true
                      },
                      // Options de qualité
                      quality: {
                        default: 720,
                        options: [4320, 2880, 2160, 1440, 1080, 720, 576, 480, 360, 240]
                      },
                      // Options de vitesse
                      speed: {
                        selected: 1,
                        options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
                      },
                      // Options de chargement
                      loadSprite: true,
                      iconUrl: 'https://cdn.plyr.io/3.7.8/plyr.svg',
                      blankVideo: 'https://cdn.plyr.io/static/blank.mp4'
                    }}
                  />
                  
                  {/* <div className="video-overlay">
                    <FaPlay className="play-icon" />
                  </div> */}
                  
                  {video.est_privee && (
                    <div className="private-badge">
                      <FaLock /> Privé
                    </div>
                  )}
                  
                  {/* <span className="video-duration">
                    {formatDuration(video.duree)}
                  </span> */}

                  {video.can_delete && (
                    <button 
                      className="btn-delete-video"
                      onClick={(e) => confirmDeleteVideo(video.id, e)}
                      title="Supprimer la vidéo"
                    >
                      <FaTrash />
                    </button>
                  )}
                </div>

                <div className="video-info">
                  <div 
                    className="video-user" 
                    onClick={() => navigate(`/profile/${video.user_id}`)}
                  >
                    <img 
                      src={getAvatarUrl(video.avatar)} 
                      alt={video.pseudo}
                      loading="lazy"
                    />
                    <div>
                      <h4 title={video.titre}>{video.titre}</h4>
                      <p>{video.pseudo}</p>
                    </div>
                  </div>

                  <div className="video-stats">
                    <span title="Vues">
                      <FaEye /> {formatNumber(video.vues)}
                    </span>
                    <span title="Likes">
                      <FaHeart className={video.user_liked ? 'liked' : ''} /> 
                      {formatNumber(video.likes)}
                    </span>
                    <span title="Date">
                      {formatDate(video.date_upload)}
                    </span>
                  </div>

                  <div className="video-actions">
                    <button 
                      className={`btn-like ${video.user_liked ? 'liked' : ''}`}
                      onClick={(e) => handleLike(video.id, e)}
                    >
                      <FaHeart /> {video.user_liked ? 'J\'aime' : 'J\'aime'}
                    </button>
                    <button 
                      className="btn-comment"
                      onClick={() => navigate(`/messages?user=${video.user_id}`)}
                    >
                      <FaComment /> Message
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Sentinel pour l'infinite scroll */}
          <div id="sentinel" className="sentinel">
            {loadingMore && (
              <div className="loading-more">
                <FaSpinner className="spinner" /> Chargement...
              </div>
            )}
            {!hasMore && videos.length > 0 && (
              <div className="no-more">
                <p>Plus aucune vidéo à charger</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Modal d'upload */}
      <Modal
        isOpen={showUploadModal}
        onClose={() => {
          if (!uploading) {
            setShowUploadModal(false);
            resetUploadForm();
          }
        }}
        title="Publier une vidéo"
        size="medium"
      >
        <form onSubmit={handleUpload} className="upload-form">
          <div className="form-group">
            <label>
              Titre de la vidéo <span className="required">*</span>
            </label>
            <input
              type="text"
              name="titre"
              value={uploadData.titre}
              onChange={handleUploadChange}
              placeholder="Ex: Massage sensuel, Soirée chaude..."
              maxLength="100"
              required
              disabled={uploading}
            />
          </div>

          <div className="form-group">
            <label>
              Fichier vidéo <span className="required">*</span>
            </label>
            <div className="file-upload-area">
              <input
                type="file"
                ref={fileInputRef}
                id="video-upload"
                accept="video/mp4,video/quicktime,video/x-msvideo"
                onChange={handleUploadChange}
                disabled={uploading}
                required
              />
              
              <label htmlFor="video-upload" className="file-upload-label">
                {uploadData.file ? (
                  <div className="file-selected">
                    <div className="file-info">
                      <span className="file-name">{uploadData.file.name.length > 50 
                        ? `${uploadData.file.name.substring(0, 47)}...` 
                        : uploadData.file.name}</span>
                      <span className="file-size">
                        {(uploadData.file.size / (1024 * 1024)).toFixed(2)} MB
                      </span>
                    </div>
                    <button 
                      type="button" 
                      className="btn-remove-file"
                      onClick={(e) => {
                        e.preventDefault();
                        setUploadData(prev => ({ ...prev, file: null }));
                        if (fileInputRef.current) {
                          fileInputRef.current.value = '';
                        }
                      }}
                      disabled={uploading}
                    >
                      <FaTimes />
                    </button>
                  </div>
                ) : (
                  <div className="file-upload-placeholder">
                    <FaUpload />
                    <span>Cliquez pour sélectionner une vidéo</span>
                    <small>MP4, MOV, AVI - Max 100MB</small>
                  </div>
                )}
              </label>
            </div>
          </div>

          {uploadProgress > 0 && (
            <div className="upload-progress">
              <div className="progress-label">
                <span>Upload en cours...</span>
                <span>{uploadProgress}%</span>
              </div>
              <div className="progress-bar">
                <div 
                  className="progress-fill" 
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
            </div>
          )}

          <div className="form-group checkbox">
            <label className="checkbox-label">
              <input
                type="checkbox"
                name="isPrivate"
                checked={uploadData.isPrivate}
                onChange={handleUploadChange}
                disabled={uploading}
              />
              <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                <span className="checkbox-custom">
                  {uploadData.isPrivate ? <FaLock /> : <FaLockOpen />}
                </span>
                <span className="checkbox-text">
                  Privée (visible uniquement par vos matches)
                </span>
              </div>
            </label>
          </div>

          <div className="form-actions">
            <button 
              type="button" 
              className="btn-cancel" 
              onClick={() => {
                setShowUploadModal(false);
                resetUploadForm();
              }}
              disabled={uploading}
            >
              Annuler
            </button>
            <button 
              type="submit" 
              className="btn-submit"
              disabled={uploading || !uploadData.file || !uploadData.titre.trim()}
            >
              {uploading ? (
                <>Publication... {uploadProgress}%</>
              ) : (
                'Publier la vidéo'
              )}
            </button>
          </div>
        </form>
      </Modal>

      {/* Modal de lecture vidéo avec Plyr en plein écran */}
      <Modal
        isOpen={showVideoModal}
        onClose={() => {
          setShowVideoModal(false);
          setSelectedVideo(null);
        }}
        title={selectedVideo?.titre?.length > 32 
          ? `${selectedVideo?.titre.substring(0, 29)}...` 
          : selectedVideo?.titre}
        size="large"
      >
        {selectedVideo && showVideoModal && (
          <div className="video-player-container">
            <Plyr
              ref={el => {
                if (el) plyrRefs.current['modal'] = el;
              }}
              source={{
                type: 'video',
                sources: [
                  {
                    src: getVideoUrl(selectedVideo),
                    type: 'video/mp4',
                  }
                ]
              }}
              options={{
                controls: [
                  'play',           // Lecture/Pause
                  'progress',       // Barre de progression
                  'current-time',   // Temps actuel
                  'duration',       // Durée totale
                  'mute',           // Muet
                  'volume',         // Volume
                  'captions',       // Sous-titres
                  'settings',       // Paramètres
                  'pip',            // Picture-in-Picture
                  'airplay',        // AirPlay
                  'fullscreen'      // Plein écran ⭐
                ],
                autoplay: true,
                muted: false,
                volume: 1,
                ratio: '16:9',
                hideControls: false,
                invertTime: false,
                toggleInvert: false,
                debug: false,
                // Options pour le plein écran
                fullscreen: {
                  enabled: true,
                  fallback: true,
                  iosNative: true
                },
                // Options de qualité
                quality: {
                  default: 720,
                  options: [4320, 2880, 2160, 1440, 1080, 720, 576, 480, 360, 240]
                },
                // Options de vitesse
                speed: {
                  selected: 1,
                  options: [0.5, 0.75, 1, 1.25, 1.5, 1.75, 2]
                },
                // Options de chargement
                loadSprite: true,
                iconUrl: 'https://cdn.plyr.io/3.7.8/plyr.svg',
                blankVideo: 'https://cdn.plyr.io/static/blank.mp4'
              }}
            />
          </div>
        )}
      </Modal>

      {/* Modal de confirmation de suppression */}
      <Modal
        isOpen={showDeleteConfirm}
        onClose={() => {
          setShowDeleteConfirm(false);
          setVideoToDelete(null);
        }}
        title="Confirmer la suppression"
        size="small"
      >
        <div className="delete-confirm-content">
          <p>Êtes-vous sûr de vouloir supprimer cette vidéo ?</p>
          <p className="delete-warning">Cette action est irréversible.</p>
          <div className="delete-confirm-actions">
            <button 
              className="btn-cancel"
              onClick={() => {
                setShowDeleteConfirm(false);
                setVideoToDelete(null);
              }}
            >
              Annuler
            </button>
            <button 
              className="btn-delete-confirm"
              onClick={handleDeleteVideo}
            >
              <FaTrash /> Supprimer
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default Videos;